import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { featureFlags } from "../../config/featureFlags";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import { GovernanceListingSection } from "../../components/governance/GovernanceListingSection";
import { ComplianceCenterMetricTile } from "../../components/compliance-center/ComplianceCenterMetricTile";
import { Button } from "../../components/ui/Button";
import GovernanceDocumentRagPanel from "../../components/governance/GovernanceDocumentRagPanel";
import GovernanceIntelligenceInteractionPanel from "../../components/governance/GovernanceIntelligenceInteractionPanel";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";

type IntelligenceListItem = {
  id: string;
  type: string;
  title: string;
  severity: string | null;
  confidence: number | null;
  lifecycleState: string;
  reviewStatus: string;
  resolutionStatus: string | null;
  createdAt: string;
  updatedAt: string;
  reasoningRunId: string | null;
};

type IntelligenceDetail = {
  id: string;
  title: string;
  description: string | null;
  type: string;
  severity: string | null;
  confidence: number | null;
  lifecycleState: string;
  reviewStatus: string;
  resolutionStatus: string | null;
  affectedEntities: Array<Record<string, unknown>>;
  citations: Array<Record<string, unknown>>;
  lineageSummary: Record<string, unknown> | null;
  recommendationSummary: Record<string, unknown> | null;
  reasoningRunId: string | null;
  createdAt: string;
  updatedAt: string;
};

type ListSummary = {
  activeInsights: number;
  activeWeaknesses: number;
  recommendations: number;
  driftAlerts: number;
  pendingReview: number;
};

type RemediationDraftSummary = {
  remediationDraftId: string;
  draftStatus: string;
  title: string;
  createdAt: string;
};

const EMPTY_FILTERS = {
  type: "",
  lifecycleState: "",
  reviewStatus: "",
  severity: "",
  confidence: "",
  search: "",
};

const REVIEW_ACTION_BUTTONS = [
  { action: "ACCEPT", label: "Accept", requiresConfirm: false },
  { action: "DISMISS", label: "Dismiss", requiresConfirm: true },
  { action: "MARK_FALSE_POSITIVE", label: "False positive", requiresConfirm: true },
  { action: "MARK_RISK_ACCEPTED", label: "Risk accepted", requiresConfirm: true },
  { action: "MARK_MITIGATED", label: "Mitigated", requiresConfirm: false },
  { action: "MARK_COMPENSATED", label: "Compensated", requiresConfirm: false },
  { action: "ARCHIVE", label: "Archive", requiresConfirm: true },
  { action: "REOPEN", label: "Reopen", requiresConfirm: false },
] as const;

function detailToListItem(detail: IntelligenceDetail): IntelligenceListItem {
  return {
    id: detail.id,
    type: detail.type,
    title: detail.title,
    severity: detail.severity,
    confidence: detail.confidence,
    lifecycleState: detail.lifecycleState,
    reviewStatus: detail.reviewStatus,
    resolutionStatus: detail.resolutionStatus,
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
    reasoningRunId: detail.reasoningRunId,
  };
}

function isRemediationDraftEligible(detail: IntelligenceDetail | null) {
  if (!detail) return false;
  const sourceType = detail.type.toLowerCase().replace(/-/g, "");
  const supported = ["insight", "weakness", "recommendation", "driftalert"].includes(sourceType);
  const reviewStatus = String(detail.reviewStatus || "").toUpperCase();
  const lifecycleState = String(detail.lifecycleState || "").toUpperCase();
  const resolutionStatus = String(detail.resolutionStatus || "").toUpperCase();
  const blocked = ["DISMISSED", "FALSE_POSITIVE"].includes(resolutionStatus);
  return (
    supported &&
    !blocked &&
    reviewStatus === "APPROVED" &&
    lifecycleState !== "ARCHIVED" &&
    ["ACCEPTED", "MITIGATED", "RISK_ACCEPTED"].includes(resolutionStatus)
  );
}

function canReviewGovernanceIntelligence(user: { role?: string; permissions?: string[] } | null) {
  const role = String(user?.role || "").toLowerCase();
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  return (
    role === "admin" ||
    permissions.includes("all") ||
    permissions.includes("admin") ||
    permissions.includes("edit.compliance")
  );
}

function formatLabel(value: string | null | undefined, fallback = "n/a") {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  return raw.replace(/_/g, " ");
}

function formatDate(value: string | null | undefined) {
  if (!value) return "n/a";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "n/a";
  return parsed.toLocaleString();
}

export default function ComplianceCenterAiGovernanceIntelligenceWorkspace() {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const surfaceEnabled = featureFlags.AI_GOVERNANCE_INTELLIGENCE_SURFACE_V1;
  const reviewActionsEnabled = featureFlags.AI_GOVERNANCE_INTELLIGENCE_REVIEW_V1;
  const remediationDraftBridgeEnabled = featureFlags.AI_GOVERNANCE_REMEDIATION_DRAFT_BRIDGE_V1;
  const canReview = canReviewGovernanceIntelligence(user);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<IntelligenceListItem[]>([]);
  const [summary, setSummary] = useState<ListSummary>({
    activeInsights: 0,
    activeWeaknesses: 0,
    recommendations: 0,
    driftAlerts: 0,
    pendingReview: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortDir, setSortDir] = useState("desc");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<IntelligenceDetail | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState<string | null>(null);
  const [reviewFeedback, setReviewFeedback] = useState<string | null>(null);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [draftNote, setDraftNote] = useState("");
  const [draftSubmitting, setDraftSubmitting] = useState(false);
  const [draftFeedback, setDraftFeedback] = useState<string | null>(null);
  const [lastRemediationDraft, setLastRemediationDraft] = useState<RemediationDraftSummary | null>(null);
  const [deriveModalOpen, setDeriveModalOpen] = useState(false);
  const [deriveSubmitting, setDeriveSubmitting] = useState(false);
  const [deriveFeedback, setDeriveFeedback] = useState<string | null>(null);
  const [deriveSummary, setDeriveSummary] = useState<{
    documentsScanned: number;
    recordsCreated: number;
    recordsSkipped: number;
    recordsDeduplicated: number;
    dryRun: boolean;
  } | null>(null);
  const [deriveSourceTypes, setDeriveSourceTypes] = useState({
    EVIDENCE: true,
    POLICY: true,
    AI_UPLOAD: true,
  });
  const [deriveMaxDocuments, setDeriveMaxDocuments] = useState(50);
  const [deriveDryRun, setDeriveDryRun] = useState(false);

  const queryString = useMemo(() => {
    const query = new URLSearchParams();
    query.set("apiVersion", "v1");
    query.set("page", String(page));
    query.set("pageSize", String(pageSize));
    query.set("sortBy", sortBy);
    query.set("sortDir", sortDir);
    if (filters.type) query.set("type", filters.type);
    if (filters.lifecycleState) query.set("lifecycleState", filters.lifecycleState);
    if (filters.reviewStatus) query.set("reviewStatus", filters.reviewStatus);
    if (filters.severity) query.set("severity", filters.severity);
    if (filters.confidence) query.set("confidence", filters.confidence);
    if (filters.search) query.set("search", filters.search);
    return query.toString();
  }, [filters, page, pageSize, sortBy, sortDir]);

  useEffect(() => {
    if (!surfaceEnabled) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_ENDPOINTS.AI_FOUNDATION_GOVERNANCE_INTELLIGENCE}?${queryString}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (response.status === 403) {
          throw new Error(
            payload?.message ||
              t(
                "complianceCenterUi.aiGovernanceIntelligenceDisabled",
                "Governance intelligence surface is disabled by feature flags.",
              ),
          );
        }
        if (!response.ok) {
          throw new Error(payload?.message || `Failed to load governance intelligence (${response.status})`);
        }
        const data = payload?.data || {};
        setItems(Array.isArray(data.items) ? data.items : []);
        setSummary({
          activeInsights: Number(data?.summary?.activeInsights || 0),
          activeWeaknesses: Number(data?.summary?.activeWeaknesses || 0),
          recommendations: Number(data?.summary?.recommendations || 0),
          driftAlerts: Number(data?.summary?.driftAlerts || 0),
          pendingReview: Number(data?.summary?.pendingReview || 0),
        });
        setTotalPages(Math.max(1, Number(data?.pagination?.totalPages || 1)));
        setTotalItems(Number(data?.pagination?.totalItems || 0));
      } catch (loadError: any) {
        setItems([]);
        setError(loadError?.message || "Failed to load governance intelligence.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [queryString, token, surfaceEnabled, t, listRefreshKey]);

  const submitReviewAction = async (action: string, requiresConfirm: boolean) => {
    if (!selectedId || !reviewActionsEnabled || !canReview) return;
    if (requiresConfirm) {
      const confirmed = window.confirm(
        t(
          "complianceCenterUi.reviewActionConfirm",
          "Apply this review action? This updates lifecycle/review state only and does not run remediation.",
        ),
      );
      if (!confirmed) return;
    }
    setReviewSubmitting(action);
    setReviewFeedback(null);
    try {
      const response = await fetch(API_ENDPOINTS.AI_FOUNDATION_GOVERNANCE_INTELLIGENCE_REVIEW_ACTION(selectedId), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action,
          note: reviewNote.trim() || undefined,
          apiVersion: "v1",
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Review action failed (${response.status})`);
      }
      const updatedDetail = payload?.data as IntelligenceDetail | undefined;
      if (updatedDetail?.id) {
        setDetail(updatedDetail);
        setItems((current) =>
          current.map((item) => (item.id === updatedDetail.id ? detailToListItem(updatedDetail) : item)),
        );
      }
      setReviewFeedback(t("complianceCenterUi.reviewActionApplied", "Review action applied."));
      setListRefreshKey((value) => value + 1);
    } catch (reviewError: any) {
      setReviewFeedback(reviewError?.message || "Failed to apply review action.");
    } finally {
      setReviewSubmitting(null);
    }
  };

  useEffect(() => {
    if (!surfaceEnabled || !selectedId) {
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      setReviewNote("");
      setReviewFeedback(null);
      setDraftNote("");
      setDraftFeedback(null);
      setLastRemediationDraft(null);
      return;
    }
    const loadDetail = async () => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        const response = await fetch(`${API_ENDPOINTS.AI_FOUNDATION_GOVERNANCE_INTELLIGENCE_DETAIL(selectedId)}?apiVersion=v1`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || `Failed to load intelligence detail (${response.status})`);
        }
        setDetail(payload?.data || null);
      } catch (loadError: any) {
        setDetail(null);
        setDetailError(loadError?.message || "Failed to load intelligence detail.");
      } finally {
        setDetailLoading(false);
      }
    };
    void loadDetail();
  }, [selectedId, token, surfaceEnabled]);

  const submitRemediationDraft = async () => {
    if (!selectedId || !remediationDraftBridgeEnabled || !canReview || !detail || !isRemediationDraftEligible(detail)) {
      return;
    }
    const confirmed = window.confirm(
      t(
        "complianceCenterUi.remediationDraftConfirm",
        "Create a remediation draft from this intelligence item? This does not execute remediation or create tasks.",
      ),
    );
    if (!confirmed) return;

    setDraftSubmitting(true);
    setDraftFeedback(null);
    try {
      const response = await fetch(
        API_ENDPOINTS.AI_FOUNDATION_GOVERNANCE_INTELLIGENCE_CREATE_REMEDIATION_DRAFT(selectedId),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            note: draftNote.trim() || undefined,
            apiVersion: "v1",
          }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Remediation draft creation failed (${response.status})`);
      }
      const draft = payload?.data;
      if (draft?.remediationDraftId) {
        setLastRemediationDraft({
          remediationDraftId: String(draft.remediationDraftId),
          draftStatus: String(draft.draftStatus || "DRAFT"),
          title: String(draft.title || detail.title),
          createdAt: String(draft.createdAt || new Date().toISOString()),
        });
      }
      setDraftFeedback(t("complianceCenterUi.remediationDraftCreated", "Remediation draft created."));
    } catch (draftError: any) {
      setDraftFeedback(draftError?.message || "Failed to create remediation draft.");
    } finally {
      setDraftSubmitting(false);
    }
  };

  const submitDerivation = async () => {
    if (!canReview) return;
    setDeriveSubmitting(true);
    setDeriveFeedback(null);
    setDeriveSummary(null);
    try {
      const sourceTypes = Object.entries(deriveSourceTypes)
        .filter(([, enabled]) => enabled)
        .map(([key]) => key);
      const response = await fetch(API_ENDPOINTS.AI_GOVERNANCE_INTELLIGENCE_DERIVE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sourceTypes: sourceTypes.length ? sourceTypes : ["EVIDENCE", "POLICY"],
          maxDocuments: deriveMaxDocuments,
          dryRun: deriveDryRun,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Derivation failed (${response.status})`);
      }
      const data = payload?.data || {};
      setDeriveSummary({
        documentsScanned: Number(data.documentsScanned || 0),
        recordsCreated: Number(data.recordsCreated || 0),
        recordsSkipped: Number(data.recordsSkipped || 0),
        recordsDeduplicated: Number(data.recordsDeduplicated || 0),
        dryRun: Boolean(deriveDryRun),
      });
      setDeriveFeedback(
        deriveDryRun
          ? t("complianceCenterUi.govDerivationDryRunDone", "Dry run completed. No records were materialized.")
          : t("complianceCenterUi.govDerivationDone", "Derivation completed. Review new intelligence records."),
      );
      if (!deriveDryRun) {
        setListRefreshKey((value) => value + 1);
      }
    } catch (deriveError: any) {
      setDeriveFeedback(deriveError?.message || "Derivation failed.");
    } finally {
      setDeriveSubmitting(false);
    }
  };

  const onSort = (nextSortBy: string) => {
    if (nextSortBy === sortBy) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(nextSortBy);
    setSortDir("desc");
    setPage(1);
  };

  if (!surfaceEnabled) {
    return (
      <div className={listStyles.pageStack}>
        <GovernanceListingSection
          title={t("complianceCenterUi.nav.aiGovernanceIntelligence", "AI Governance Intelligence")}
          description={t(
            "complianceCenterUi.aiGovernanceIntelligenceDisabledIntro",
            "This read-only intelligence surface is disabled. Enable VITE_AI_GOVERNANCE_INTELLIGENCE_SURFACE_V1 to view materialized governance intelligence.",
          )}
        >
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 m-0">
            {t(
              "complianceCenterUi.aiGovernanceIntelligenceFlagHint",
              "No mutation, chat, or autonomous actions are available in this workspace.",
            )}
          </p>
        </GovernanceListingSection>
      </div>
    );
  }

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t("complianceCenterUi.nav.aiGovernanceIntelligence", "AI Governance Intelligence")}
        description={t(
          "complianceCenterUi.aiGovernanceIntelligenceIntro",
          "Read-only governance intelligence surface with citations and lineage summaries.",
        )}
      >
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid5}`}>
          <ComplianceCenterMetricTile
            label={t("complianceCenterUi.aiGovActiveInsights", "Active insights")}
            value={loading ? "…" : summary.activeInsights}
            tone="blue"
          />
          <ComplianceCenterMetricTile
            label={t("complianceCenterUi.aiGovActiveWeaknesses", "Active weaknesses")}
            value={loading ? "…" : summary.activeWeaknesses}
            tone="red"
          />
          <ComplianceCenterMetricTile
            label={t("complianceCenterUi.aiGovRecommendations", "AI Governance Recommendations")}
            value={loading ? "…" : summary.recommendations}
            tone="purple"
          />
          <ComplianceCenterMetricTile
            label={t("complianceCenterUi.aiGovDriftAlerts", "Drift alerts")}
            value={loading ? "…" : summary.driftAlerts}
            tone="orange"
          />
          <ComplianceCenterMetricTile
            label={t("complianceCenterUi.aiGovPendingReview", "Pending review")}
            value={loading ? "…" : summary.pendingReview}
            tone="yellow"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Link to="/compliance-center/ai-governance-conversation">
            <Button type="button" size="small" color="outline">
              {t("complianceCenterUi.nav.aiCiso", "Open AI CISO")}
            </Button>
          </Link>
        {canReview ? (
          <>
            <Button type="button" size="small" color="primary" onClick={() => setDeriveModalOpen(true)}>
              {t("complianceCenterUi.govDerivationAction", "Derive intelligence from documents")}
            </Button>
            <p className="text-xs text-slate-500 m-0">
              {t(
                "complianceCenterUi.govDerivationHint",
                "User-triggered only. Does not change evidence or control status.",
              )}
            </p>
          </>
        ) : null}
        </div>
      </GovernanceListingSection>

      {deriveModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gov-derivation-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5">
            <h2 id="gov-derivation-title" className="text-lg font-semibold m-0 mb-2">
              {t("complianceCenterUi.govDerivationModalTitle", "Derive governance intelligence")}
            </h2>
            <p className="text-sm text-slate-600 m-0 mb-4">
              {t(
                "complianceCenterUi.govDerivationModalIntro",
                "Analyze indexed organization documents and materialize reviewable intelligence. Non-authoritative; human review required.",
              )}
            </p>
            <fieldset className="border-0 p-0 m-0 mb-3">
              <legend className="text-sm font-medium mb-2">
                {t("complianceCenterUi.govDerivationSourceTypes", "Source types")}
              </legend>
              <div className="flex flex-col gap-2 text-sm">
                {(["EVIDENCE", "POLICY", "AI_UPLOAD"] as const).map((key) => (
                  <label key={key} className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={deriveSourceTypes[key]}
                      onChange={(e) =>
                        setDeriveSourceTypes((prev) => ({ ...prev, [key]: e.target.checked }))
                      }
                    />
                    {key === "EVIDENCE"
                      ? t("complianceCenterUi.govDerivationEvidence", "Evidence")
                      : key === "POLICY"
                        ? t("complianceCenterUi.govDerivationPolicy", "Policies")
                        : t("complianceCenterUi.govDerivationAiUpload", "AI uploads")}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="block text-sm font-medium mb-1">
              {t("complianceCenterUi.govDerivationMaxDocuments", "Max documents")}
            </label>
            <input
              className="input w-full mb-3"
              type="number"
              min={1}
              max={200}
              value={deriveMaxDocuments}
              onChange={(e) => setDeriveMaxDocuments(Math.max(1, Math.min(200, Number(e.target.value) || 50)))}
            />
            <label className="inline-flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={deriveDryRun}
                onChange={(e) => setDeriveDryRun(e.target.checked)}
              />
              {t("complianceCenterUi.govDerivationDryRun", "Dry run (preview counts only)")}
            </label>
            {deriveSummary ? (
              <div className="text-sm bg-slate-50 border border-slate-200 rounded p-3 mb-3">
                <p className="m-0">
                  {t("complianceCenterUi.govDerivationScanned", "Documents scanned")}: {deriveSummary.documentsScanned}
                </p>
                <p className="m-0">
                  {t("complianceCenterUi.govDerivationCreated", "Records created")}: {deriveSummary.recordsCreated}
                </p>
                <p className="m-0">
                  {t("complianceCenterUi.govDerivationSkipped", "Skipped")}: {deriveSummary.recordsSkipped}
                </p>
                <p className="m-0">
                  {t("complianceCenterUi.govDerivationDeduped", "Deduplicated")}: {deriveSummary.recordsDeduplicated}
                </p>
              </div>
            ) : null}
            {deriveFeedback ? (
              <p className="text-sm text-slate-700 m-0 mb-3" role="status">
                {deriveFeedback}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="small"
                color="outline"
                onClick={() => {
                  setDeriveModalOpen(false);
                  setDeriveFeedback(null);
                  setDeriveSummary(null);
                }}
                disabled={deriveSubmitting}
              >
                {t("common.close", "Close")}
              </Button>
              <Button type="button" size="small" color="primary" onClick={() => void submitDerivation()} disabled={deriveSubmitting}>
                {deriveSubmitting
                  ? t("complianceCenterUi.govDerivationRunning", "Running…")
                  : t("complianceCenterUi.govDerivationRun", "Run derivation")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <GovernanceListingSection title={t("complianceCenterUi.filters", "Filters")}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select className="input" value={filters.type} onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}>
            <option value="">{t("complianceCenterUi.allTypes", "All types")}</option>
            <option value="insight">{t("complianceCenterUi.typeInsight", "Insight")}</option>
            <option value="weakness">{t("complianceCenterUi.typeWeakness", "Weakness")}</option>
            <option value="recommendation">{t("complianceCenterUi.typeRecommendation", "Recommendation")}</option>
            <option value="narrative">{t("complianceCenterUi.typeNarrative", "Narrative")}</option>
            <option value="driftAlert">{t("complianceCenterUi.typeDriftAlert", "Drift alert")}</option>
          </select>
          <select
            className="input"
            value={filters.lifecycleState}
            onChange={(e) => setFilters((prev) => ({ ...prev, lifecycleState: e.target.value }))}
          >
            <option value="">{t("complianceCenterUi.allLifecycle", "All lifecycle")}</option>
            <option value="NEW">NEW</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="SUPERSEDED">SUPERSEDED</option>
            <option value="STALE">STALE</option>
            <option value="RESOLVED">RESOLVED</option>
            <option value="ARCHIVED">ARCHIVED</option>
            <option value="REJECTED">REJECTED</option>
          </select>
          <select
            className="input"
            value={filters.reviewStatus}
            onChange={(e) => setFilters((prev) => ({ ...prev, reviewStatus: e.target.value }))}
          >
            <option value="">{t("complianceCenterUi.allReview", "All review")}</option>
            <option value="PENDING_REVIEW">PENDING_REVIEW</option>
            <option value="APPROVED">APPROVED</option>
            <option value="REJECTED">REJECTED</option>
            <option value="ESCALATED">ESCALATED</option>
          </select>
          <input
            className="input"
            placeholder={t("complianceCenterUi.severity", "Severity")}
            value={filters.severity}
            onChange={(e) => setFilters((prev) => ({ ...prev, severity: e.target.value }))}
          />
          <input
            className="input"
            placeholder={t("complianceCenterUi.confidence", "Confidence (number or level)")}
            value={filters.confidence}
            onChange={(e) => setFilters((prev) => ({ ...prev, confidence: e.target.value }))}
          />
          <input
            className="input"
            placeholder={t("complianceCenterUi.searchCitations", "Search title/citations")}
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setPage(1);
            }}
          >
            {t("complianceCenterUi.resetFilters", "Reset filters")}
          </Button>
          <Button
            type="button"
            size="small"
            color="outline"
            onClick={() => {
              setPage(1);
            }}
          >
            {t("complianceCenterUi.applyFilters", "Apply")}
          </Button>
        </div>
      </GovernanceListingSection>

      <GovernanceListingSection title={t("complianceCenterUi.intelligenceList", "Intelligence list")}>
        {error ? (
          <p className="text-sm text-red-600 m-0 mb-2" role="alert">
            {error}
          </p>
        ) : null}
        {loading ? (
          <p className="text-sm text-slate-500 m-0" aria-live="polite">
            {t("common.loading", "Loading…")}
          </p>
        ) : null}
        {!loading && !error && !items.length ? (
          <p className="text-sm text-slate-500 m-0 italic">
            {t("complianceCenterUi.noIntelligenceRecords", "No intelligence records found for the current filters.")}
          </p>
        ) : null}
        {items.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="py-2 pr-3">
                    <button type="button" className="font-semibold" onClick={() => onSort("type")}>
                      {t("complianceCenterUi.columnType", "Type")}
                    </button>
                  </th>
                  <th className="py-2 pr-3">
                    <button type="button" className="font-semibold" onClick={() => onSort("title")}>
                      {t("complianceCenterUi.columnTitle", "Title")}
                    </button>
                  </th>
                  <th className="py-2 pr-3">{t("complianceCenterUi.columnSeverity", "Severity")}</th>
                  <th className="py-2 pr-3">
                    <button type="button" className="font-semibold" onClick={() => onSort("confidence")}>
                      {t("complianceCenterUi.columnConfidence", "Confidence")}
                    </button>
                  </th>
                  <th className="py-2 pr-3">{t("complianceCenterUi.columnLifecycle", "Lifecycle")}</th>
                  <th className="py-2 pr-3">{t("complianceCenterUi.columnReview", "Review")}</th>
                  <th className="py-2 pr-3">
                    <button type="button" className="font-semibold" onClick={() => onSort("createdAt")}>
                      {t("complianceCenterUi.columnCreated", "Created")}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={`border-b border-slate-100 cursor-pointer ${selectedId === item.id ? "bg-blue-50" : ""}`}
                    onClick={() => setSelectedId(item.id)}
                  >
                    <td className="py-2 pr-3">{formatLabel(item.type)}</td>
                    <td className="py-2 pr-3">{item.title}</td>
                    <td className="py-2 pr-3">{formatLabel(item.severity)}</td>
                    <td className="py-2 pr-3">{item.confidence ?? "n/a"}</td>
                    <td className="py-2 pr-3">{formatLabel(item.lifecycleState)}</td>
                    <td className="py-2 pr-3">{formatLabel(item.reviewStatus)}</td>
                    <td className="py-2 pr-3">{formatDate(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <Button type="button" size="small" color="outline" disabled={loading || page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              {t("common.prev", "Prev")}
            </Button>
            <span className="text-xs text-slate-600">
              {t("complianceCenterUi.pageOf", "Page")} {page} / {totalPages}
              {totalItems ? ` · ${totalItems} ${t("complianceCenterUi.totalItems", "items")}` : ""}
            </span>
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={loading || page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("common.next", "Next")}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-600" htmlFor="ai-governance-page-size">
              {t("complianceCenterUi.pageSize", "Page size")}
            </label>
            <select
              id="ai-governance-page-size"
              className="input"
              value={pageSize}
              disabled={loading}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </GovernanceListingSection>

      <GovernanceListingSection
        title={t("complianceCenterUi.detailDrawer", "Detail drawer (read-only)")}
        toolbar={
          selectedId ? (
            <Button type="button" size="small" color="outline" onClick={() => setSelectedId(null)}>
              {t("common.close", "Close")}
            </Button>
          ) : null
        }
      >
        {!selectedId ? (
          <p className="text-sm text-slate-500 m-0">
            {t("complianceCenterUi.selectIntelligenceHint", "Select an item to inspect details.")}
          </p>
        ) : null}
        {detailLoading ? (
          <p className="text-sm text-slate-500 m-0" aria-live="polite">
            {t("complianceCenterUi.loadingDetail", "Loading detail…")}
          </p>
        ) : null}
        {detailError ? (
          <p className="text-sm text-red-600 m-0" role="alert">
            {detailError}
          </p>
        ) : null}
        {detail ? (
          <div className="space-y-3 text-sm text-slate-700">
            <div>
              <div className="font-semibold">{detail.title}</div>
              <div className="text-xs text-slate-500">{formatLabel(detail.type)}</div>
            </div>

            <section className="rounded border border-slate-200 bg-slate-50/60 p-3 space-y-1" aria-label="Review status">
              <div className="font-semibold text-slate-800">
                {t("complianceCenterUi.reviewStatusBlock", "Review status")}
              </div>
              <div>
                {t("complianceCenterUi.columnReview", "Review")}: {formatLabel(detail.reviewStatus)}
              </div>
              <div className="text-xs text-slate-500">
                {t("complianceCenterUi.updated", "Updated")}: {formatDate(detail.updatedAt)}
              </div>
            </section>

            <section
              className="rounded border border-slate-200 bg-slate-50/60 p-3 space-y-1"
              aria-label="Lifecycle and resolution"
            >
              <div className="font-semibold text-slate-800">
                {t("complianceCenterUi.lifecycleResolutionBlock", "Lifecycle & resolution")}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  {t("complianceCenterUi.columnLifecycle", "Lifecycle")}: {formatLabel(detail.lifecycleState)}
                </div>
                <div>
                  {t("complianceCenterUi.resolution", "Resolution")}: {formatLabel(detail.resolutionStatus)}
                </div>
                <div>
                  {t("complianceCenterUi.columnSeverity", "Severity")}: {formatLabel(detail.severity)}
                </div>
                <div>
                  {t("complianceCenterUi.columnConfidence", "Confidence")}: {detail.confidence ?? "n/a"}
                </div>
              </div>
              <div className="text-xs text-slate-500">
                {t("complianceCenterUi.reasoningRun", "Reasoning run")}: {detail.reasoningRunId || "n/a"}
              </div>
              {lastRemediationDraft ? (
                <div className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs text-purple-800">
                  {t("complianceCenterUi.remediationDraftBadge", "Remediation draft")}: {formatLabel(lastRemediationDraft.draftStatus)}
                </div>
              ) : null}
            </section>

            <p className="m-0">{detail.description || t("complianceCenterUi.noDescription", "No description.")}</p>
            <GovernanceIntelligenceInteractionPanel
              intelligenceId={detail.id}
              intelligenceType={detail.type}
              intelligenceTitle={detail.title}
              linkedCitationCount={Array.isArray(detail.citations) ? detail.citations.length : 0}
            />
            <div>
              <div className="font-semibold">{t("complianceCenterUi.affectedEntities", "Affected entities")}</div>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 overflow-auto m-0">
                {JSON.stringify(detail.affectedEntities || [], null, 2)}
              </pre>
            </div>
            <div>
              <div className="font-semibold">{t("complianceCenterUi.citations", "Citations")}</div>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 overflow-auto m-0">
                {JSON.stringify(detail.citations || [], null, 2)}
              </pre>
            </div>
            <GovernanceDocumentRagPanel
              title={t("complianceCenterUi.analyzeSupportingDocuments", "Analyze supporting documents")}
              intent="governance_document_analysis"
              presetQueries={[
                {
                  label: t("complianceCenterUi.explainRecommendation", "Explain recommendation"),
                  query: `Explain governance intelligence item "${detail.title}" using supporting documents only.`,
                },
                {
                  label: t("complianceCenterUi.showEvidenceGrounding", "Show evidence grounding"),
                  query: `What document evidence supports "${detail.title}"? Cite excerpts only.`,
                },
              ]}
              governanceContext={{
                intelligenceId: detail.id,
                type: detail.type,
                title: detail.title,
                reasoningRunId: detail.reasoningRunId,
              }}
            />
            <div>
              <div className="font-semibold">{t("complianceCenterUi.lineageSummary", "Lineage summary")}</div>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 overflow-auto m-0">
                {JSON.stringify(detail.lineageSummary || {}, null, 2)}
              </pre>
            </div>
            <div>
              <div className="font-semibold">{t("complianceCenterUi.recommendationSummary", "Recommendation summary")}</div>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-2 overflow-auto m-0">
                {JSON.stringify(detail.recommendationSummary || {}, null, 2)}
              </pre>
            </div>
            <div className="text-xs text-slate-500">
              {t("complianceCenterUi.created", "Created")}: {formatDate(detail.createdAt)} | {t("complianceCenterUi.updated", "Updated")}:{" "}
              {formatDate(detail.updatedAt)}
            </div>
            {selectedId && reviewActionsEnabled && canReview ? (
              <section
                className="rounded border border-slate-200 pt-3 px-3 pb-3 space-y-2"
                aria-label="Human review actions"
              >
                <div className="font-semibold text-slate-800">
                  {t("complianceCenterUi.humanReviewActions", "Human review actions")}
                </div>
                <p className="text-xs text-slate-500 m-0">
                  {t(
                    "complianceCenterUi.humanReviewActionsHint",
                    "Controlled lifecycle updates only. No auto-remediation, workflow execution, or task creation.",
                  )}
                </p>
                <label className="block text-xs font-medium text-slate-600" htmlFor="ai-governance-review-note">
                  {t("complianceCenterUi.reviewNote", "Review note")}
                </label>
                <textarea
                  id="ai-governance-review-note"
                  className="input w-full min-h-[72px]"
                  placeholder={t("complianceCenterUi.reviewNotePlaceholder", "Optional note for audit trail")}
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  disabled={Boolean(reviewSubmitting)}
                />
                <div className="flex flex-wrap gap-2">
                  {REVIEW_ACTION_BUTTONS.map((entry) => (
                    <Button
                      key={entry.action}
                      type="button"
                      size="small"
                      color="outline"
                      disabled={Boolean(reviewSubmitting)}
                      onClick={() => void submitReviewAction(entry.action, entry.requiresConfirm)}
                    >
                      {reviewSubmitting === entry.action ? t("common.loading", "Loading…") : entry.label}
                    </Button>
                  ))}
                </div>
                {reviewFeedback ? <p className="text-xs text-slate-600 m-0">{reviewFeedback}</p> : null}
              </section>
            ) : null}
            {selectedId &&
            remediationDraftBridgeEnabled &&
            canReview &&
            detail &&
            isRemediationDraftEligible(detail) ? (
              <section
                className="rounded border border-purple-200 bg-purple-50/40 pt-3 px-3 pb-3 space-y-2"
                aria-label="Remediation draft bridge"
              >
                <div className="font-semibold text-slate-800">
                  {t("complianceCenterUi.remediationDraftBridge", "Remediation draft bridge")}
                </div>
                <p className="text-xs text-slate-500 m-0">
                  {t(
                    "complianceCenterUi.remediationDraftBridgeHint",
                    "Human-approved draft only. No auto-remediation, workflow execution, or task assignment.",
                  )}
                </p>
                <label className="block text-xs font-medium text-slate-600" htmlFor="ai-governance-draft-note">
                  {t("complianceCenterUi.remediationDraftNote", "Draft note")}
                </label>
                <textarea
                  id="ai-governance-draft-note"
                  className="input w-full min-h-[72px]"
                  placeholder={t("complianceCenterUi.remediationDraftNotePlaceholder", "Optional note for draft audit trail")}
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                  disabled={draftSubmitting}
                />
                <div className="flex flex-wrap gap-2 items-center">
                  <Button
                    type="button"
                    size="small"
                    color="brand"
                    disabled={draftSubmitting}
                    onClick={() => void submitRemediationDraft()}
                  >
                    {draftSubmitting
                      ? t("common.loading", "Loading…")
                      : t("complianceCenterUi.createRemediationDraft", "Create remediation draft")}
                  </Button>
                  {lastRemediationDraft ? (
                    <Link
                      className="text-xs text-purple-800 underline"
                      to={`/compliance-center/remediation?governanceDraftId=${encodeURIComponent(lastRemediationDraft.remediationDraftId)}`}
                    >
                      {t("complianceCenterUi.openRemediationDraft", "Open remediation workspace")}
                    </Link>
                  ) : null}
                </div>
                {draftFeedback ? <p className="text-xs text-slate-600 m-0">{draftFeedback}</p> : null}
              </section>
            ) : null}
          </div>
        ) : null}
      </GovernanceListingSection>
    </div>
  );
}
